import { UserRole } from '../ducks/auth/types';

export enum Permission {
  // CRUD Operations
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  
  // Special permissions
  EXPORT = 'export',
  IMPORT = 'import',
  MANAGE_USERS = 'manage_users',
  VIEW_ANALYTICS = 'view_analytics',
}

export enum Resource {
  PROPERTIES = 'properties',
  CLIENTS = 'clients',
  OWNERS = 'owners',
  FEATURES = 'features',
  POPULAR_PLACES = 'popular_places',
  BLOGS = 'blogs',
  HOMEPAGE_IMAGES = 'homepage_images',
  MEMBERS = 'members',
  DASHBOARD = 'dashboard',
}

// Define role permissions matrix
export const ROLE_PERMISSIONS: Record<UserRole, Record<Resource, Permission[]>> = {
  [UserRole.ADMIN]: {
    [Resource.PROPERTIES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.EXPORT],
    [Resource.CLIENTS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.OWNERS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.FEATURES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.POPULAR_PLACES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.BLOGS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.HOMEPAGE_IMAGES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.MEMBERS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.MANAGE_USERS],
    [Resource.DASHBOARD]: [Permission.READ, Permission.VIEW_ANALYTICS],
  },
  [UserRole.MANAGER]: {
    [Resource.PROPERTIES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.EXPORT],
    [Resource.CLIENTS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.OWNERS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.FEATURES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.POPULAR_PLACES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.BLOGS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.HOMEPAGE_IMAGES]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
    [Resource.MEMBERS]: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.MANAGE_USERS],
    [Resource.DASHBOARD]: [Permission.READ, Permission.VIEW_ANALYTICS],
  },
  [UserRole.ASSISTANT]: {
    [Resource.PROPERTIES]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.CLIENTS]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.OWNERS]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.FEATURES]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.POPULAR_PLACES]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.BLOGS]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.HOMEPAGE_IMAGES]: [Permission.CREATE, Permission.READ, Permission.UPDATE],
    [Resource.MEMBERS]: [Permission.READ],
    [Resource.DASHBOARD]: [Permission.READ],
  },
  [UserRole.NORMAL_USER]: {
    [Resource.PROPERTIES]: [Permission.CREATE, Permission.READ],
    [Resource.CLIENTS]: [Permission.CREATE, Permission.READ],
    [Resource.OWNERS]: [Permission.CREATE, Permission.READ],
    [Resource.FEATURES]: [Permission.CREATE, Permission.READ],
    [Resource.POPULAR_PLACES]: [Permission.CREATE, Permission.READ],
    [Resource.BLOGS]: [Permission.CREATE, Permission.READ],
    [Resource.HOMEPAGE_IMAGES]: [Permission.CREATE, Permission.READ],
    [Resource.MEMBERS]: [Permission.READ],
    [Resource.DASHBOARD]: [Permission.READ],
  },
};

export const hasPermission = (
  userRole: UserRole,
  resource: Resource,
  permission: Permission
): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  if (!rolePermissions) return false;
  
  const resourcePermissions = rolePermissions[resource];
  if (!resourcePermissions) return false;
  
  return resourcePermissions.includes(permission);
};

export const getPermissions = (
  userRole: UserRole,
  resource: Resource
): Permission[] => {
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  if (!rolePermissions) return [];
  
  return rolePermissions[resource] || [];
}; 